import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Header,
  Input,
  Label,
  Message,
  Pagination,
  Segment,
  Select,
  Table,
  TextArea
} from 'semantic-ui-react';
import { API, isRoot, showError, showInfo, showSuccess, timestamp2string } from '../../helpers';
import { ITEMS_PER_PAGE } from '../../constants';
import { renderQuota, renderQuotaNumber } from '../../helpers/render';

// 微信支付订单状态（与后端 model.PaymentOrderStatus 保持一致）
const PAYMENT_STATUS_MAP = {
  1: { text: '待支付', color: 'yellow' },
  2: { text: '已支付', color: 'green' },
  3: { text: '已过期', color: 'grey' }
};

const ManualTopUp = () => {
  const [userKeyword, setUserKeyword] = useState('');
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [amount, setAmount] = useState('100');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [historyKeyword, setHistoryKeyword] = useState('');
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [editingNote, setEditingNote] = useState('');

  const [wechatKeyword, setWechatKeyword] = useState('');
  const [wechatOrders, setWechatOrders] = useState([]);
  const [wechatLoading, setWechatLoading] = useState(true);
  const [wechatActivePage, setWechatActivePage] = useState(1);

  const quotaPerUnit = useMemo(() => {
    const value = parseFloat(localStorage.getItem('quota_per_unit'));
    if (!value || value <= 0) return 500000;
    return value;
  }, []);

  const previewQuota = useMemo(() => {
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum <= 0) return 0;
    return Math.round(amountNum * quotaPerUnit);
  }, [amount, quotaPerUnit]);

  const loadGrants = async (startIdx, keyword = historyKeyword) => {
    const trimmedKeyword = keyword.trim();
    const res = await API.get(`/api/manual_topup/?p=${startIdx}&keyword=${encodeURIComponent(trimmedKeyword)}`);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setGrants(data);
      } else {
        let newGrants = [...grants];
        newGrants.splice(startIdx * ITEMS_PER_PAGE, data.length, ...data);
        setGrants(newGrants);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const refreshHistory = async (keyword = historyKeyword) => {
    setLoading(true);
    setActivePage(1);
    await loadGrants(0, keyword);
  };

  useEffect(() => {
    refreshHistory().then();
  }, []);

  const loadWechatOrders = async (startIdx, keyword = wechatKeyword) => {
    const trimmedKeyword = keyword.trim();
    const res = await API.get(`/api/payment/orders?p=${startIdx}&keyword=${encodeURIComponent(trimmedKeyword)}`);
    const { success, message, data } = res.data;
    if (success) {
      const orders = data || [];
      if (startIdx === 0) {
        setWechatOrders(orders);
      } else {
        const newOrders = [...wechatOrders];
        newOrders.splice(startIdx * ITEMS_PER_PAGE, orders.length, ...orders);
        setWechatOrders(newOrders);
      }
    } else {
      showError(message);
    }
    setWechatLoading(false);
  };

  const refreshWechatOrders = async (keyword = wechatKeyword) => {
    setWechatLoading(true);
    setWechatActivePage(1);
    await loadWechatOrders(0, keyword);
  };

  useEffect(() => {
    refreshWechatOrders().then();
  }, []);

  const onWechatPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(wechatOrders.length / ITEMS_PER_PAGE) + 1) {
        await loadWechatOrders(activePage - 1);
      }
      setWechatActivePage(activePage);
    })();
  };

  const saveNote = async (id) => {
    const res = await API.patch(`/api/manual_topup/${id}`, { note: editingNote });
    const { success, message } = res.data;
    if (!success) { showError(message); return; }
    setGrants(grants.map(g => g.id === id ? { ...g, note: editingNote } : g));
    setEditingId(null);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(grants.length / ITEMS_PER_PAGE) + 1) {
        await loadGrants(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  const searchUsers = async () => {
    if (!userKeyword.trim()) {
      showInfo('请输入用户名、邮箱或显示名称');
      return;
    }
    setSearchingUsers(true);
    try {
      const res = await API.get(`/api/user/search?keyword=${encodeURIComponent(userKeyword.trim())}`);
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      const options = data.map((user) => ({
        key: user.id,
        text: `${user.username}（ID: ${user.id}，余额: ${renderQuota(user.quota)}）`,
        value: user.id
      }));
      setUserOptions(options);
      if (options.length === 1) {
        setSelectedUserId(options[0].value);
      }
      if (options.length === 0) {
        showInfo('没有找到匹配用户');
      }
    } catch (error) {
      showError(error);
    } finally {
      setSearchingUsers(false);
    }
  };

  const createGrant = async () => {
    if (!selectedUserId) {
      showInfo('请先搜索并选择用户');
      return;
    }

    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum < 1) {
      showInfo('充值金额须不小于 1 元');
      return;
    }

    setSubmitting(true);
    try {
      const res = await API.post('/api/manual_topup', {
        user_id: selectedUserId,
        amount_fen: Math.round(amountNum * 100),
        note
      });
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      showSuccess(`已为 ${data.username} 增加额度 ${renderQuota(data.granted_quota)}`);
      setAmount('100');
      setNote('');
      await refreshHistory(historyKeyword);
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isRoot()) {
    return (
      <Segment>
        <Message negative>仅超级管理员可访问该页面。</Message>
      </Segment>
    );
  }

  return (
    <>
      <Segment>
        <Header as='h3'>银行转账人工充值</Header>
        <Message info>
          root 用户可在确认线下到账后，手动为指定用户增加额度。系统会保留专门的转账充值记录和充值日志。
        </Message>
        <Form>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>搜索用户</label>
              <Input
                placeholder='用户名 / 邮箱 / 显示名称'
                value={userKeyword}
                onChange={(e) => setUserKeyword(e.target.value)}
                action={{
                  color: 'blue',
                  content: '搜索',
                  onClick: searchUsers,
                  loading: searchingUsers
                }}
              />
            </Form.Field>
            <Form.Field>
              <label>选择用户</label>
              <Select
                placeholder='先搜索再选择'
                options={userOptions}
                value={selectedUserId}
                onChange={(e, { value }) => setSelectedUserId(value)}
              />
            </Form.Field>
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>转账金额（元）</label>
              <Input
                type='number'
                min='1'
                step='0.01'
                value={amount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || val === '-') {
                    setAmount('');
                    return;
                  }
                  const parts = val.split('.');
                  const formatted =
                    parts.length > 1 ? parts[0] + '.' + parts[1].slice(0, 2) : val;
                  setAmount(formatted);
                }}
                label='¥'
                labelPosition='left'
              />
            </Form.Field>
            <Form.Field>
              <label>预计发放额度</label>
              <div style={{ paddingTop: '9px' }}>
                <Label size='large' color='teal'>
                  {previewQuota > 0 ? renderQuotaNumber(previewQuota) : '—'}
                </Label>
              </div>
            </Form.Field>
          </Form.Group>
          <Form.Field
            control={TextArea}
            label='备注'
            placeholder='可选，例如银行流水号、确认人、备注信息'
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button color='green' onClick={createGrant} loading={submitting} disabled={submitting}>
            发放额度
          </Button>
        </Form>
      </Segment>

      <Segment>
        <Header as='h3'>人工充值记录</Header>
        <Form>
          <Form.Group>
            <Form.Input
              fluid
              width={6}
              label='搜索用户名 / 操作人'
              placeholder='前缀匹配'
              value={historyKeyword}
              onChange={(e, { value }) => setHistoryKeyword(value)}
            />
            <Form.Button fluid label='操作' width={2} onClick={() => refreshHistory(historyKeyword)} loading={loading}>
              查询
            </Form.Button>
          </Form.Group>
        </Form>

        <Table basic compact size='small'>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>时间</Table.HeaderCell>
              <Table.HeaderCell>用户</Table.HeaderCell>
              <Table.HeaderCell>操作人</Table.HeaderCell>
              <Table.HeaderCell>金额</Table.HeaderCell>
              <Table.HeaderCell>额度</Table.HeaderCell>
              <Table.HeaderCell>备注</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {grants
              .slice(
                (activePage - 1) * ITEMS_PER_PAGE,
                activePage * ITEMS_PER_PAGE
              )
              .map((grant) => (
                <Table.Row key={grant.id}>
                  <Table.Cell>{timestamp2string(grant.created_at)}</Table.Cell>
                  <Table.Cell>{grant.username}</Table.Cell>
                  <Table.Cell>{grant.operator_username}</Table.Cell>
                  <Table.Cell>¥{(grant.amount_fen / 100).toFixed(2)}</Table.Cell>
                  <Table.Cell>{renderQuota(grant.granted_quota, 6)}</Table.Cell>
                  <Table.Cell>
                    {editingId === grant.id ? (
                      <Input
                        size='mini'
                        value={editingNote}
                        onChange={(e) => setEditingNote(e.target.value)}
                        action={{
                          size: 'mini', icon: 'check', color: 'green',
                          onClick: () => saveNote(grant.id)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveNote(grant.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                      />
                    ) : (
                      <span
                        style={{ cursor: 'pointer' }}
                        onClick={() => { setEditingId(grant.id); setEditingNote(grant.note || ''); }}
                        title='点击编辑备注'
                      >
                        {grant.note || '-'}
                      </span>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>

          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='6'>
                <Pagination
                  floated='right'
                  activePage={activePage}
                  onPageChange={onPaginationChange}
                  size='small'
                  siblingRange={1}
                  totalPages={
                    Math.ceil(grants.length / ITEMS_PER_PAGE) +
                    (grants.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                  }
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </Segment>

      <Segment>
        <Header as='h3'>微信支付记录</Header>
        <Message info>
          用户扫码支付的订单明细，可用于与微信商户平台对账。状态为“待支付”的订单超过 2 小时会自动过期。
        </Message>
        <Form>
          <Form.Group>
            <Form.Input
              fluid
              width={6}
              label='搜索用户名 / 订单号'
              placeholder='前缀匹配'
              value={wechatKeyword}
              onChange={(e, { value }) => setWechatKeyword(value)}
            />
            <Form.Button
              fluid
              label='操作'
              width={2}
              onClick={() => refreshWechatOrders(wechatKeyword)}
              loading={wechatLoading}
            >
              查询
            </Form.Button>
          </Form.Group>
        </Form>

        <Table basic compact size='small'>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>时间</Table.HeaderCell>
              <Table.HeaderCell>订单号</Table.HeaderCell>
              <Table.HeaderCell>用户</Table.HeaderCell>
              <Table.HeaderCell>金额</Table.HeaderCell>
              <Table.HeaderCell>额度</Table.HeaderCell>
              <Table.HeaderCell>状态</Table.HeaderCell>
              <Table.HeaderCell>支付时间</Table.HeaderCell>
              <Table.HeaderCell>微信交易号</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {wechatOrders
              .slice(
                (wechatActivePage - 1) * ITEMS_PER_PAGE,
                wechatActivePage * ITEMS_PER_PAGE
              )
              .map((order) => {
                const status = PAYMENT_STATUS_MAP[order.status] || {
                  text: '未知',
                  color: 'grey'
                };
                return (
                  <Table.Row key={order.id}>
                    <Table.Cell>{timestamp2string(order.created_time)}</Table.Cell>
                    <Table.Cell>{order.order_no}</Table.Cell>
                    <Table.Cell>{order.username || '-'}</Table.Cell>
                    <Table.Cell>¥{(order.amount_fen / 100).toFixed(2)}</Table.Cell>
                    <Table.Cell>{renderQuota(order.quota, 6)}</Table.Cell>
                    <Table.Cell>
                      <Label size='small' color={status.color}>
                        {status.text}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      {order.paid_time ? timestamp2string(order.paid_time) : '-'}
                    </Table.Cell>
                    <Table.Cell>{order.wechat_transaction_id || '-'}</Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>

          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='8'>
                <Pagination
                  floated='right'
                  activePage={wechatActivePage}
                  onPageChange={onWechatPaginationChange}
                  size='small'
                  siblingRange={1}
                  totalPages={
                    Math.ceil(wechatOrders.length / ITEMS_PER_PAGE) +
                    (wechatOrders.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                  }
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </Segment>
    </>
  );
};

export default ManualTopUp;
